import { X, Plus } from "@phosphor-icons/react";
import { DialogHeader } from "@shopflo/ui/dialog";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { DialogBody } from "components/common/dialogs/GenericDialog";
import InputField from "components/common/forms/InputField";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { postRequest } from "lib/core/apiClient";
import { useLocale } from "lib/hooks/useLocale";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { apiURI, IMAGE_CDN_URL } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import { PaymentMode } from "lib/types/payments";
import React, { useEffect, useState } from "react";
import { mutate } from "swr";

interface RedeemVoucherDialogProps {
  gyftrBalance: number;
  setModalType: React.Dispatch<React.SetStateAction<"ADD" | "REDEEM">>;
}

const RedeemGyftrContent: React.FC<RedeemVoucherDialogProps> = ({ gyftrBalance, setModalType }) => {
  const { t } = useLocale();
  const {
    state: { checkoutId, billing },
    actions: { setCheckoutModal, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { paymentMethods },
    actions: { mutatePayment },
  } = usePaymentContext();

  const gyftrMode = paymentMethods?.find((method: PaymentMode) => method.name === "GYFTR");

  const [values, setValues] = useState({
    redeemAmount: Math.min(billing.total_payable, gyftrBalance).toString(),
    error: "",
    loading: false,
  });

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      redeemAmount: Math.min(billing.total_payable, gyftrBalance).toString(),
    }));
  }, [billing.total_payable, gyftrBalance]);

  const handleRedeem = async () => {
    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
    if (gyftrMode && !gyftrMode.isAvailable && gyftrMode?.unavailabilityReasons.length) {
      setCheckoutModal("GYFTR_SWITCH_COUPON_DIALOG");
      return;
    }
    setValues((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const response: any = await postRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        redemption_amount: Number(values.redeemAmount),
        voucher_payment_mode: "GYFTR",
      });

      if (response.status && response.status === "FAILED") {
        setValues((prev) => ({
          ...prev,
          error: response.error,
        }));
        return;
      }

      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
      mutate("gc/voucher/balance");
      setCheckoutModal("NONE");
    } catch (err: any) {
      console.error("error", err);
      setValues((prev) => ({
        ...prev,
        error: err.response.data.error,
      }));
    } finally {
      setValues((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      setValues({ ...values, redeemAmount: "0" });
      return;
    }

    const newAmount = Number(inputValue);
    if (isNaN(newAmount)) return;

    const maxAllowedAmount = Math.max(0, Math.min(newAmount, billing.total_payable, gyftrBalance));

    setValues({
      ...values,
      redeemAmount: inputValue.includes(".")
        ? (newAmount > maxAllowedAmount ? maxAllowedAmount : inputValue).toString().slice(0, 15)
        : maxAllowedAmount.toString(),
    });
  };

  const handleUseMax = () => {
    setValues({ ...values, redeemAmount: Math.min(billing.total_payable, gyftrBalance).toString() });
  };

  return (
    <>
      <div className="relative flex w-full flex-col gap-4 px-5 pb-8 pt-4">
        <button className="outline-none absolute top-4 right-4  ">
          <X
            className="h-5 w-5 cursor-pointer text-coal-dark"
            onClick={(e) => {
              e.stopPropagation();
              setCheckoutModal("NONE");
            }}
          />
        </button>
        <div className="py-3 px-2 flex gap-5">
          <img src={`${IMAGE_CDN_URL}/gyftr.png`} alt="" className="w-12 h-12" />
          <div className="flex flex-col gap-1">
            <p className="text-xs text-gray-dark">{t("gyftr_e_pay_balance")}</p>
            <h4 className="text-medium font-semibold text-carbon-dark">{currencyFormatter(gyftrBalance)}</h4>
            <h4
              className="text-sm text-primary-dark font-semibold cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setModalType("ADD");
              }}>
              {t("add_balance")}
            </h4>
          </div>
        </div>
        <div className="">
          <div className="relative">
            <InputField
              type="number"
              label=""
              id="redeem_amount"
              name="redeemAmount"
              height="h-12"
              value={values.redeemAmount.toString()}
              onChange={handleAmountChange}
              filled={Boolean(values.redeemAmount)}
              autoComplete="off"
              required={false}
              startIcon={<span className="text-coal-dark">₹</span>}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <button
                className="text-primary-dark font-medium min-w-[80px]"
                disabled={values.loading}
                onClick={handleUseMax}>
                {t("use_max")}
              </button>
            </div>
          </div>
          {values.error && <p className="text-xs text-red-dark px-1 mt-1">{values.error}</p>}
        </div>
        <PrimaryButton
          buttonText={t("redeem_epay_voucher")}
          onClick={handleRedeem}
          onTouchStart={handleRedeem}
          height="h-12"
          isLoading={values.loading}
          isDisabled={values.redeemAmount === '0'}
        />
      </div>
    </>
  );
};

export default RedeemGyftrContent;
